import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '@components/ui/Card';
import Button from '@components/ui/Button';
import Input from '@components/ui/Input';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import './Projects.css';
import './PeopleForm.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FormState {
  project_name: string;
  status: string;
  project_type: string;
  cost_model: string;
  general_notes: string;
  estimated_start_date: string;
  estimated_close_date: string;
}

interface FormErrors {
  project_name?: string;
  company_id?: string;
}

interface CompanySuggestion {
  id: string;
  company_name: string;
}

interface PersonSuggestion {
  id: string;
  full_name: string;
  company_name: string | null;
}

const PROJECT_STATUSES = [
  'New',
  'Proposal Accepted',
  'Work Started',
  'Work Finished',
  'Completed',
] as const;

// ─── Component ────────────────────────────────────────────────────────────────

const ProjectForm: React.FC = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>({
    project_name: '',
    status: 'New',
    project_type: '',
    cost_model: '',
    general_notes: '',
    estimated_start_date: '',
    estimated_close_date: '',
  });

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  // Company selection state
  const [companySearch, setCompanySearch] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  const [companySuggestions, setCompanySuggestions] = useState<CompanySuggestion[]>([]);
  const [showCompanySuggestions, setShowCompanySuggestions] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  // Lead person selection state
  const [personSearch, setPersonSearch] = useState('');
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [selectedPersonName, setSelectedPersonName] = useState('');
  const [personSuggestions, setPersonSuggestions] = useState<PersonSuggestion[]>([]);
  const [showPersonSuggestions, setShowPersonSuggestions] = useState(false);
  const [loadingPeople, setLoadingPeople] = useState(false);

  // ─── Company search ───────────────────────────────────────────────────────────

  const searchCompanies = useCallback(async (query: string) => {
    if (!query.trim() || query.trim().length < 2) {
      setCompanySuggestions([]);
      setShowCompanySuggestions(false);
      return;
    }
    setLoadingCompanies(true);
    try {
      const res = await fetch(`/api/companies?q=${encodeURIComponent(query)}&page_size=5`);
      if (!res.ok) return;
      const json = await res.json() as { success: boolean; data: CompanySuggestion[] };
      if (json.success) {
        setCompanySuggestions(json.data);
        setShowCompanySuggestions(json.data.length > 0);
      }
    } catch {
      // Silently fail
    } finally {
      setLoadingCompanies(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchCompanies(companySearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [companySearch, searchCompanies]);

  const selectCompany = (company: CompanySuggestion) => {
    setSelectedCompanyId(company.id);
    setSelectedCompanyName(company.company_name);
    setCompanySearch(company.company_name);
    setCompanySuggestions([]);
    setShowCompanySuggestions(false);
    // Clear errors
    setErrors((prev) => ({ ...prev, company_id: undefined }));
  };

  // ─── Person search ────────────────────────────────────────────────────────────

  const searchPeople = useCallback(async (query: string) => {
    if (!query.trim() || query.trim().length < 2) {
      setPersonSuggestions([]);
      setShowPersonSuggestions(false);
      return;
    }
    setLoadingPeople(true);
    try {
      const res = await fetch(`/api/people?q=${encodeURIComponent(query)}&page_size=5`);
      if (!res.ok) return;
      const json = await res.json() as { success: boolean; data: PersonSuggestion[] };
      if (json.success) {
        setPersonSuggestions(json.data);
        setShowPersonSuggestions(json.data.length > 0);
      }
    } catch {
      // Silently fail
    } finally {
      setLoadingPeople(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchPeople(personSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [personSearch, searchPeople]);

  const selectPerson = (person: PersonSuggestion) => {
    setSelectedPersonId(person.id);
    setSelectedPersonName(person.full_name);
    setPersonSearch(person.full_name);
    setPersonSuggestions([]);
    setShowPersonSuggestions(false);
  };

  // ─── Validation ──────────────────────────────────────────────────────────────

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!form.project_name.trim()) {
      newErrors.project_name = 'Project name is required.';
    }
    if (!selectedCompanyId) {
      newErrors.company_id = 'Please select a company.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ─── Submit ──────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    setSaveError(null);

    try {
      const res = await fetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_name: form.project_name.trim(),
          company_id: selectedCompanyId,
          lead_person_id: selectedPersonId || null,
          status: form.status,
          project_type: form.project_type.trim() || null,
          cost_model: form.cost_model.trim() || null,
          general_notes: form.general_notes.trim() || null,
          estimated_start_date: form.estimated_start_date.trim() || null,
          estimated_close_date: form.estimated_close_date.trim() || null,
        }),
      });

      const json = await res.json() as {
        success: boolean;
        data?: { id: string };
        message?: string;
      };

      if (!res.ok || !json.success) {
        throw new Error(json.message ?? `HTTP ${res.status}`);
      }

      navigate(`/projects/${json.data!.id}`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create project.';
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="project-form-page">
      {/* Back navigation */}
      <button className="back-link" onClick={() => navigate('/projects')} type="button">
        ← Projects
      </button>

      {/* Page header */}
      <div className="form-page-header">
        <h1 className="form-page-title">New project</h1>
      </div>

      <Card>
        <form onSubmit={handleSubmit} noValidate>
          <div className="form-fields">

            {/* Project name */}
            <Input
              label="Project name *"
              value={form.project_name}
              onChange={(e) => setForm({ ...form, project_name: e.target.value })}
              placeholder="e.g. Acme Corp Website Redesign"
              error={errors.project_name}
              autoFocus
            />

            {/* Company with search */}
            <div className="company-field-wrapper">
              <Input
                label="Company *"
                value={companySearch}
                onChange={(e) => {
                  setCompanySearch(e.target.value);
                  // If user edits after selecting, clear the selection
                  if (selectedCompanyName && e.target.value !== selectedCompanyName) {
                    setSelectedCompanyId(null);
                    setSelectedCompanyName('');
                  }
                }}
                onBlur={() => {
                  setTimeout(() => setShowCompanySuggestions(false), 150);
                }}
                onFocus={() => {
                  if (companySuggestions.length > 0) setShowCompanySuggestions(true);
                }}
                placeholder="Search for a company…"
                autoComplete="off"
                error={errors.company_id}
              />
              {loadingCompanies && (
                <div className="company-suggestions-loading">
                  <LoadingSpinner size="sm" />
                </div>
              )}
              {showCompanySuggestions && companySuggestions.length > 0 && (
                <ul className="company-suggestions">
                  {companySuggestions.map((company) => (
                    <li key={company.id}>
                      <button
                        type="button"
                        className="company-suggestion-item"
                        onMouseDown={() => selectCompany(company)}
                      >
                        {company.company_name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {selectedCompanyId && (
                <p className="company-field-hint" style={{ color: 'var(--color-success)' }}>
                  ✓ {selectedCompanyName} selected
                </p>
              )}
            </div>

            {/* Lead person with search */}
            <div className="company-field-wrapper">
              <Input
                label="Lead person"
                value={personSearch}
                onChange={(e) => {
                  setPersonSearch(e.target.value);
                  if (selectedPersonName && e.target.value !== selectedPersonName) {
                    setSelectedPersonId(null);
                    setSelectedPersonName('');
                  }
                }}
                onBlur={() => {
                  setTimeout(() => setShowPersonSuggestions(false), 150);
                }}
                onFocus={() => {
                  if (personSuggestions.length > 0) setShowPersonSuggestions(true);
                }}
                placeholder="Search for a person…"
                autoComplete="off"
              />
              {loadingPeople && (
                <div className="company-suggestions-loading">
                  <LoadingSpinner size="sm" />
                </div>
              )}
              {showPersonSuggestions && personSuggestions.length > 0 && (
                <ul className="company-suggestions">
                  {personSuggestions.map((person) => (
                    <li key={person.id}>
                      <button
                        type="button"
                        className="company-suggestion-item"
                        onMouseDown={() => selectPerson(person)}
                      >
                        {person.full_name}
                        {person.company_name && (
                          <span style={{ color: 'var(--color-text-light)', marginLeft: '0.5rem', fontSize: '0.875rem' }}>
                            — {person.company_name}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {selectedPersonId && (
                <p className="company-field-hint" style={{ color: 'var(--color-success)' }}>
                  ✓ {selectedPersonName} selected
                </p>
              )}
            </div>

            {/* Status */}
            <div className="input-wrapper">
              <label className="input-label">Status</label>
              <select
                className="input"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                {PROJECT_STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* Project type */}
            <Input
              label="Project type"
              value={form.project_type}
              onChange={(e) => setForm({ ...form, project_type: e.target.value })}
              placeholder="e.g. Discovery, Build, Retainer"
            />

            {/* Cost model */}
            <Input
              label="Cost model"
              value={form.cost_model}
              onChange={(e) => setForm({ ...form, cost_model: e.target.value })}
              placeholder="e.g. Fixed price, Time and materials"
            />

            {/* Dates */}
            <Input
              label="Estimated start date"
              type="date"
              value={form.estimated_start_date}
              onChange={(e) => setForm({ ...form, estimated_start_date: e.target.value })}
            />

            <Input
              label="Estimated close date"
              type="date"
              value={form.estimated_close_date}
              onChange={(e) => setForm({ ...form, estimated_close_date: e.target.value })}
            />

            {/* Notes */}
            <div className="input-wrapper">
              <label className="input-label">Notes</label>
              <textarea
                className="input form-textarea"
                value={form.general_notes}
                onChange={(e) => setForm({ ...form, general_notes: e.target.value })}
                placeholder="General notes about this project…"
                rows={5}
              />
            </div>

            {saveError && (
              <p className="form-save-error">{saveError}</p>
            )}

            <div className="form-actions">
              <Button
                type="submit"
                variant="primary"
                disabled={saving}
              >
                {saving ? 'Creating…' : 'Create project'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/projects')}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ProjectForm;
